import { useState } from "react";
import { X, MapPin, Home, DollarSign, TrendingUp, Calendar, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WizardFormData, ADU_TYPES, FINISH_LEVELS } from "@/types/wizard";

interface Step8ResultsProps {
  formData: WizardFormData;
  parcelData: any;
  isDark: boolean;
}

const Step8Results = ({ formData, parcelData, isDark }: Step8ResultsProps) => {
  const [showCalendly, setShowCalendly] = useState(false);

  const cardStyles = {
    backgroundColor: isDark ? "#0a0a0a" : "#ffffff",
    boxShadow: isDark
      ? "4px 4px 8px #000000, -4px -4px 8px #141414"
      : "4px 4px 8px #d1d1d1, -4px -4px 8px #ffffff",
  };

  // Calculate estimated costs and ROI
  const squareFootage = parseInt(formData.squareFootage) || 750;
  const finishLevel = FINISH_LEVELS.find(l => l.value === formData.finishLevel);
  const materialCost = finishLevel?.cost || 99;
  const baseRate = 450; // Base design-build rate
  const totalCostPerSqFt = baseRate + materialCost;
  const totalInvestment = squareFootage * totalCostPerSqFt;
  
  // ROI calculations (simplified)
  const estimatedMonthlyRent = Math.round(squareFootage * 4.5); // ~$4.50/sqft in Bay Area
  const annualRent = estimatedMonthlyRent * 12;
  const tenYearROI = Math.round(((annualRent * 10) / totalInvestment) * 100);

  const aduType = ADU_TYPES.find(t => t.value === formData.aduType);

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 
          className="text-2xl md:text-3xl font-bold mb-2"
          style={{ color: "#E4683B" }}
        >
          🎉 Your ADU Assessment Results
        </h2>
        <p 
          className="text-lg opacity-80"
          style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
        >
          Here's what we found for your property
        </p>
      </div>

      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Property Overview */}
        <div className="p-6 rounded-2xl" style={cardStyles}>
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-6 h-6" style={{ color: "#E4683B" }} />
            <h3 className="text-xl font-bold" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
              Property Details
            </h3>
          </div>
          <div className="space-y-3" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
            <p><strong>Address:</strong> {formData.propertyData?.address}</p>
            <p><strong>City:</strong> {formData.propertyData?.city}</p>
            <p><strong>County:</strong> {formData.propertyData?.county}</p>
            {parcelData && (
              <>
                <p><strong>Lot Size:</strong> {parcelData.ll_gissqft ? `${parcelData.ll_gissqft.toLocaleString()} sq ft` : 'N/A'}</p>
                <p><strong>Zoning:</strong> {parcelData.zoning || 'Residential'}</p>
              </>
            )}
          </div>
        </div>

        {/* ADU Selection */}
        <div className="p-6 rounded-2xl" style={cardStyles}>
          <div className="flex items-center gap-3 mb-4">
            <Home className="w-6 h-6" style={{ color: "#E4683B" }} />
            <h3 className="text-xl font-bold" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
              Your ADU Plan
            </h3>
          </div>
          <div className="space-y-3" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
            <p><strong>Type:</strong> {aduType?.label || 'Not specified'}</p>
            <p><strong>Size:</strong> {squareFootage} sq ft</p>
            <p><strong>Finish Level:</strong> {finishLevel?.label || 'Standard'}</p>
          </div>
        </div>

        {/* Investment Summary */}
        <div className="p-6 rounded-2xl" style={cardStyles}>
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-6 h-6" style={{ color: "#E4683B" }} />
            <h3 className="text-xl font-bold" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
              Estimated Investment
            </h3>
          </div>
          <div className="space-y-3" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
            <p className="text-3xl font-bold" style={{ color: "#E4683B" }}>
              ${totalInvestment.toLocaleString()}
            </p>
            <p className="text-sm opacity-70">
              ${totalCostPerSqFt}/sq ft (design-build + materials)
            </p>
          </div>
        </div>

        {/* ROI Summary */}
        <div className="p-6 rounded-2xl" style={cardStyles}>
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="w-6 h-6" style={{ color: "#E4683B" }} />
            <h3 className="text-xl font-bold" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
              Return on Investment
            </h3>
          </div>
          <div className="space-y-3" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
            <div className="flex justify-between items-center">
              <span>Est. Monthly Rent:</span>
              <span className="font-bold">${estimatedMonthlyRent.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span>Annual Income:</span>
              <span className="font-bold">${annualRent.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span>10-Year ROI:</span>
              <span className="font-bold text-2xl" style={{ color: "#E4683B" }}>{tenYearROI}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Eligibility Checklist */}
      <div className="max-w-4xl mx-auto p-6 rounded-2xl" style={cardStyles}>
        <h3 
          className="text-xl font-bold mb-4 flex items-center gap-3"
          style={{ color: "#E4683B" }}
        >
          <Check className="w-6 h-6" />
          Eligibility Checklist
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}>
          {[
            "Property is zoned for residential use",
            "Lot meets minimum size requirements",
            "ADU size within city limits",
            "Owner-occupied property eligible",
            "Setback requirements achievable",
            "Utility connections available",
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "#E4683B" }}
              >
                <Check className="w-4 h-4 text-white" />
              </div>
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA - Schedule Consultation */}
      <div className="max-w-4xl mx-auto text-center p-8 rounded-2xl" style={cardStyles}>
        <Calendar className="w-12 h-12 mx-auto mb-4" style={{ color: "#E4683B" }} />
        <h3 
          className="text-2xl font-bold mb-2"
          style={{ color: "#E4683B" }}
        >
          Ready to get started?
        </h3>
        <p 
          className="mb-6 opacity-80"
          style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
        >
          Schedule a free consultation with our ADU experts to discuss pricing, 
          exclusive deals, promotions, and grants available for your project.
        </p>
        <Button
          onClick={() => setShowCalendly(true)}
          className="px-8 py-4 rounded-xl font-semibold text-lg"
          style={{ backgroundColor: "#E4683B", color: "#ffffff" }}
        >
          <Calendar className="w-5 h-5 mr-2" />
          Schedule Free Consultation
        </Button>
      </div>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={() => setShowCalendly(false)} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Step8Results;
